'use client'

import { useState, useEffect, Fragment, useMemo } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { BellIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@clerk/nextjs'
import { useRouter } from 'next/navigation'
import { createPortal } from 'react-dom'
import { useStations } from '@/contexts/stations-context'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function NotificationDropdown() {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [mounted, setMounted] = useState(false)
  const { getToken } = useAuth()
  const { stations } = useStations()
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  const fetchNotifications = async () => {
    try {
      const token = await getToken()
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API}/notifications/active`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setNotifications(data.notifications || [])
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  // Transform stations with alert event names into displayable alerts
  const nwsAlerts = useMemo(() => {
    if (!stations) return []
    
    const allAlerts = []
    stations.forEach(station => {
      if (station.alerts && Array.isArray(station.alerts) && station.alerts.length > 0) {
        // Create an alert entry for each alert object
        station.alerts.forEach((alert, alertIndex) => {
          allAlerts.push({
            event: typeof alert === 'string' ? alert : alert.event,
            station: {
              id: station.id,
              name: station.name,
              internalId: station.internalId
            },
            alertIndex: alertIndex
          })
        })
      }
    })
    
    return allAlerts
  }, [stations])

  useEffect(() => {
    fetchNotifications()
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications()
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  const activeCount = notifications.filter(n => n.active).length
  const nwsAlertsCount = nwsAlerts.length
  const totalActiveCount = activeCount + nwsAlertsCount

  const handleNotificationClick = (notification) => {
    router.push(`/stations/${notification.station.internalId}`)
  }

  const handleAlertClick = (alert) => {
    router.push(`/stations/${alert.station.internalId}/notifications`)
  }

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500 dark:text-gray-400 dark:hover:text-white relative flex items-center justify-center">
        <span className="sr-only">View notifications</span>
        <BellIcon aria-hidden="true" className="size-6" />
        {totalActiveCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {totalActiveCount > 9 ? '9+' : totalActiveCount}
          </span>
        )}
      </Menu.Button>

      {mounted && createPortal(
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="fixed right-4 top-16 z-[9999] w-96 origin-top-right rounded-md bg-white dark:bg-gray-800 py-2 shadow-lg ring-1 ring-gray-900/5 dark:ring-white/10 focus:outline-none max-h-96 overflow-y-auto">
            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Active Alerts & Notifications
              </h3>
            </div>

            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading alerts...
              </div>
            ) : totalActiveCount === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No active alerts
              </div>
            ) : (
              <div className="py-1">
                {/* NWS Active Alerts */}
                {nwsAlerts.map((alert, index) => (
                  <Menu.Item key={`alert-${alert.station.id}-${alert.alertIndex}-${index}`}>
                    {({ active }) => (
                      <button
                        onClick={() => handleAlertClick(alert)}
                        className={classNames(
                          active ? 'bg-gray-50 dark:bg-gray-700' : '',
                          'block w-full px-4 py-3 text-left text-sm border-b border-gray-100 dark:border-gray-700'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {alert.station.name}
                            </p>
                            <p className="text-gray-600 dark:text-gray-300 mt-0.5 truncate">
                              {alert.event}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <span className="inline-flex items-center rounded-full bg-red-50 dark:bg-red-900/20 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-400">
                              NWS
                            </span>
                          </div>
                        </div>
                      </button>
                    )}
                  </Menu.Item>
                ))}

                {/* Custom Notifications */}
                {notifications
                  .filter(n => n.active)
                  .map((notification) => (
                    <Menu.Item key={notification.id}>
                      {({ active }) => (
                        <button
                          onClick={() => handleNotificationClick(notification)}
                          className={classNames(
                            active ? 'bg-gray-50 dark:bg-gray-700' : '',
                            'block w-full px-4 py-3 text-left text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0'
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 dark:text-white truncate">
                                {notification.station.name}
                              </p>
                              <p className="text-gray-600 dark:text-gray-300 mt-0.5 truncate">
                                {notification.name}
                              </p>
                              {notification.lastTriggeredAt && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {new Date(notification.lastTriggeredAt).toLocaleString()}
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/20 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400">
                                Custom
                              </span>
                            </div>
                          </div>
                        </button>
                      )}
                    </Menu.Item>
                  ))}
              </div>
            )}
          </Menu.Items>
      </Transition>,
      document.body
      )}
    </Menu>
  )
}
